import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken } from '../../utils';
import DecimalField from '../shared/DecimalField';

function EnableInventoryDialog({ t, open, onClose, part, onSuccess }) {
  const { currentOrganization } = useUserStore();
  const [initialQuantity, setInitialQuantity] = useState('0');
  const [reorderPoint, setReorderPoint] = useState('');
  const [shelfLocation, setShelfLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setInitialQuantity('0');
    setReorderPoint('');
    setShelfLocation('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (initialQuantity === '' || parseFloat(initialQuantity) < 0) {
      setError(t('Please enter a valid quantity'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await axios.post(
        `/api/parts/${part.id}/enable-inventory/?organization=${currentOrganization.organization_id}`,
        {
          initial_quantity: initialQuantity,
          reorder_point: reorderPoint || null,
          shelf_location: shelfLocation || '',
        },
        {
          headers: {
            'X-CSRFToken': getCsrfToken(),
          },
        }
      );

      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error('Failed to enable inventory:', err);
      setError(err.response?.data?.error || t('Failed to enable inventory tracking'));
    } finally {
      setLoading(false);
    }
  };

  if (!part) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('inventory.enableTracking')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {part.name}
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('inventory.enableTrackingDescription')}
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <DecimalField
            label={t('inventory.initialQuantity')}
            value={initialQuantity}
            onChange={setInitialQuantity}
            required
            autoFocus
            fullWidth
            sx={{ mb: 2 }}
            helperText={t('inventory.initialQuantityHelp')}
          />

          <DecimalField
            label={t('inventory.reorderPoint')}
            value={reorderPoint}
            onChange={setReorderPoint}
            fullWidth
            sx={{ mb: 2 }}
            helperText={t('inventory.reorderPointHelp')}
          />

          <TextField
            label={t('inventory.shelfLocation')}
            value={shelfLocation}
            onChange={(e) => setShelfLocation(e.target.value)}
            fullWidth
            size="small"
            helperText={t('inventory.shelfLocationHelp')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || initialQuantity === ''}
        >
          {loading ? <CircularProgress size={20} /> : t('inventory.enableTracking')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(EnableInventoryDialog);
